import { ParentProps, createContext, Show, createEffect, For } from 'solid-js'
import { requireContext, date } from 'common'
import styles from './st-attendance-grid.module.css'
import { CalendarDisplayMode } from '../st-schedule-grid/st-schedule-grid';
import attendanceGridApi from '../../api/st-attendance-grid';

// Fragment imports
import { WeekNavPrev, WeekNavNext } from './Fragments/WeekNavigation';
import { WeekSelector } from './Fragments/WeekSelector';
import { AttendanceLegend } from './Fragments/AttendanceLegend';
import { WeekHeader } from './Fragments/WeekHeader';
import { CalendarContent } from './Fragments/CalendarContent';
import { SignatureModalWrapper } from './Fragments/SignatureModalWrapper';
import { AttendanceGridContext } from './AttendanceGridContext';
import { GetStaffAttendanceDataRequest, GetStaffAttendanceDataResponse } from '../../api';
import Button from '../st-button/st-button';
import Loader from '../st-loader/st-loader';
import Select from '../st-select/st-select';
import { CalendarService } from './services/CalendarService';
import { SectionVerificationService } from './services/SectionVerificationService';

const AttendanceGridContextJSX = createContext<AttendanceGridContext>();
export const requireAttendanceGridContext = () => {
    return requireContext(AttendanceGridContextJSX);
}

export class AttendanceGridProps
{
    displayMode: CalendarDisplayMode = 'WorkWeek';
    viewId: string = '';
    objectId: string = '';
    identity: string = '';
    afterAddClicked?: boolean = false;
    enabled: boolean = false;
}

export default function AttendanceGrid(props: ParentProps<AttendanceGridProps>) {
    const context = new AttendanceGridContext();

    return (
        <AttendanceGridContextJSX.Provider value={context}>
            <AttendanceGridInternal {...props} />
        </AttendanceGridContextJSX.Provider>
    )
}

function AttendanceGridInternal(props: ParentProps<AttendanceGridProps>)
{
    const ctx = requireAttendanceGridContext();

    // Load attendance codes on mount
    createEffect(async () => {
        try {
            const codesResponse = await attendanceGridApi.GetAttendanceCodes();
            if (codesResponse.success && codesResponse.attendanceCodes) {
                const codesMap = new Map<string, typeof codesResponse.attendanceCodes[0]>();
                codesResponse.attendanceCodes.forEach(code => {
                    codesMap.set(code.code ?? '', code);
                });
                ctx.attendanceCodesMap[1](codesMap);
            }
        } catch (error) {
            console.error('Failed to load attendance codes:', error);
        }
    });

    createEffect(async() =>
    {
        // Wait until focus date is loaded before fetching attendance
        if (!ctx.focusDateLoaded[0]()) {
            return;
        }

        ctx.isLoading[1](true);
        try {
            const startDate = date.addDays(date.startOfWeek(ctx.day()), 1); // Monday
            const endDate = date.addDays(date.startOfWeek(ctx.day()), 5);   // Friday

            const request: GetStaffAttendanceDataRequest = {
                startDate: CalendarService.formatDateForApi(startDate),
                endDate: CalendarService.formatDateForApi(endDate)
            }

            // Check if this week already has a verification
            await ctx.checkWeekVerification(startDate, endDate);

            const staffData: GetStaffAttendanceDataResponse = await attendanceGridApi.GetStudentAttendance(request);
            ctx.attendance[1](staffData.staffSectionData);

            // Capture staffSchoolYearGU and attendanceType for report generation
            if (staffData.staffSchoolYearGU) {
                ctx.staffSchoolYearGU[1](staffData.staffSchoolYearGU);
            }
            if (staffData.attendanceType != null) {
                ctx.attendanceType[1](staffData.attendanceType);
            }

            // Set valid school week flag (disables confirm buttons for holiday/non-school weeks)
            ctx.isValidSchoolWeek[1](staffData.isValidSchoolWeek ?? true);

            // Restore any Pending draft confirmations saved from a previous session
            const restorations = ctx.pendingSectionRestorations[0]();
            if (restorations.length > 0) {
                ctx.attendance[1](prev =>
                    SectionVerificationService.restoreSectionVerifications(prev, restorations)
                );
                ctx.pendingSectionRestorations[1]([]);
            }
        } finally {
            ctx.isLoading[1](false);
        }
    })

    return(
        <div class={`${styles.schedule} ${styles['main-container']} flexbox vertical`}>
            <SignatureModalWrapper />
            {/* Verified banner — only shown when no re-verification is in progress */}
            <Show when={ctx.hasVerification[0]() && !ctx.isPending[0]()}>
                <div class={ctx.verificationStatus[0]() === '1'
                    ? styles['verification-banner-denied']
                    : styles['verification-banner']}>
                    <div class={styles['verification-banner-content']}>
                        <span class={styles['verification-banner-icon']}>
                            {ctx.verificationStatus[0]() === '1' ? '!' : '✓'}
                        </span>
                        <div class={styles['verification-banner-text']}>
                            <Show when={ctx.verificationStatus[0]() === '1'} fallback={
                                <>
                                    <strong>This week has been verified</strong>
                                    <p>All attendance has been confirmed and submitted.</p>
                                </>
                            }>
                                <strong>This week has been submitted with denied sections</strong>
                                <p>Your verification has been recorded. One or more sections were denied and will require further review.</p>
                            </Show>
                        </div>
                    </div>
                </div>
            </Show>
            {/* Re-verification / in-progress notice — shown whenever a Pending draft exists */}
            <Show when={ctx.isPending[0]()}>
                <div class={styles['reverification-banner']}>
                    <div class={styles['verification-banner-content']}>
                        <span class={styles['verification-banner-icon']}>ℹ</span>
                        <div class={styles['verification-banner-text']}>
                            <Show when={ctx.hasVerification[0]()} fallback={
                                <>
                                    <strong>Verification in progress</strong>
                                    <p>You have confirmed one or more sections. Complete all sections and submit to finalize.</p>
                                </>
                            }>
                                <strong>Re-verification in progress</strong>
                                <p>A verified record already exists for this week. You are creating a new submission.</p>
                            </Show>
                        </div>
                    </div>
                    <Button type="default" onClick={() => ctx.cancelReVerification()} enabled={!ctx.isProcessingReport[0]()}>
                        Cancel
                    </Button>
                </div>
            </Show>
            <div class={styles['sticky-header']}>
                <div class={styles['week-nav-wrapper']}>
                    <WeekNavPrev />
                    <WeekSelector />
                    <WeekNavNext />
                </div>
                <div class={styles['submit-container']}>
                    <label class={styles['doc-category-field']}>
                        <span class={styles['doc-category-label']}>Document Category:</span>
                        <Select value={ctx.selectedDocCategory}>
                            <For each={ctx.documentCategoryOptions[0]()}>{(opt) => (
                                <Select.Option value={opt.value ?? ''}>
                                    <span>{opt.description ?? opt.value}</span>
                                </Select.Option>
                            )}</For>
                        </Select>
                    </label>
                    <Show when={ctx.hasDocument[0]()}>
                        <Button
                            type="primary"
                            onClick={() => ctx.downloadDocument()}
                        >
                            Download Verified Report
                        </Button>
                    </Show>
                    <Button
                        type="primary"
                        onClick={() => ctx.handleSubmit()}
                        enabled={ctx.allSectionsReviewed() && !ctx.isProcessingReport[0]() && !!ctx.selectedDocCategory[0]()}
                    >
                        {ctx.isProcessingReport[0]() ? 'Processing...' : 'Submit'}
                    </Button>
                </div>
            </div>
            <AttendanceLegend />
            <div class={styles['calendar-sections']}>
                <Show when={ctx.isLoading[0]()} fallback={
                    <For each={ctx.attendance[0]()}>
                        {(section) => (
                            <>
                                <WeekHeader
                                    courseName={section.courseName ?? ''}
                                    courseID={section.courseID ?? ''}
                                    sectionName={section.sectionName ?? ''}
                                    periodNumber={section.periodNumber ?? ''}
                                    sectionGU={section.sectionGU ?? ''}
                                />
                                <CalendarContent sectGU={section.sectionGU ?? ''} />
                            </>
                        )}
                    </For>
                }>
                    <div class={styles['loader-container']}>
                        <Loader size="large" label="Loading attendance data..." />
                    </div>
                </Show>
            </div>
        </div>
    )
}

AttendanceGrid.defaultProps = new AttendanceGridProps();